/**
 * PrefetchLink — drop-in replacement for react-router's Link / NavLink
 * that warms the destination route's code chunk on hover/focus.
 *
 * Use anywhere a sidebar/navbar Link wants instant navigation feel.
 */
import { forwardRef, useCallback } from "react";
import { Link, NavLink, LinkProps, NavLinkProps } from "react-router-dom";
import { prefetchRoute } from "@/lib/routePrefetch";

const usePrefetchHandlers = (to: LinkProps["to"]) => {
  const handler = useCallback(() => {
    if (typeof to === "string") prefetchRoute(to);
  }, [to]);
  return { onMouseEnter: handler, onFocus: handler, onTouchStart: handler };
};

export const PrefetchLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, onMouseEnter, onFocus, onTouchStart, ...props }, ref) => {
    const handlers = usePrefetchHandlers(to);
    return (
      <Link
        ref={ref}
        to={to}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          onMouseEnter?.(e);
        }}
        onFocus={(e) => {
          handlers.onFocus();
          onFocus?.(e);
        }}
        onTouchStart={(e) => {
          handlers.onTouchStart();
          onTouchStart?.(e);
        }}
        {...props}
      />
    );
  },
);
PrefetchLink.displayName = "PrefetchLink";

export const PrefetchNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, onMouseEnter, onFocus, onTouchStart, ...props }, ref) => {
    const handlers = usePrefetchHandlers(to);
    return (
      <NavLink
        ref={ref}
        to={to}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          onMouseEnter?.(e);
        }}
        onFocus={(e) => {
          handlers.onFocus();
          onFocus?.(e);
        }}
        onTouchStart={(e) => {
          handlers.onTouchStart();
          onTouchStart?.(e);
        }}
        {...props}
      />
    );
  },
);
PrefetchNavLink.displayName = "PrefetchNavLink";
